import { z } from 'zod';

const OfferStatusSchema = z.enum(['draft', 'published', 'internal', 'closed', 'archived']);

const AvatarSchema = z.object({
  name: z.string(),
  avatarUrl: z.string().nullish(),
  initials: z.string().nullish(),
});

const PersonSubtitleSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('text'), text: z.string() }),
  z.object({ kind: z.literal('job-ref'), title: z.string(), status: OfferStatusSchema }),
  z.object({ kind: z.literal('jobs-count'), count: z.number().int().nonnegative() }),
]);

const ItemBadgeSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('lead') }),
  z.object({ kind: z.literal('overdue'), days: z.number().int().positive() }),
]);

const CandidateDataSchema = z.object({
  id: z.string(),
  name: z.string(),
  avatarUrl: z.string().nullish(),
  initials: z.string().nullish(),
  subtitle: PersonSubtitleSchema.nullish(),
  badge: ItemBadgeSchema.nullish(),
});

const TeamMemberDataSchema = z.object({
  id: z.string(),
  name: z.string(),
  avatarUrl: z.string().nullish(),
  initials: z.string().nullish(),
  subtitle: PersonSubtitleSchema.nullish(),
});

const JobDataSchema = z.object({
  id: z.string(),
  title: z.string(),
  slug: z.string(),
  status: OfferStatusSchema,
  candidatesCount: z.number().int().nonnegative().nullish(),
  location: z.string().nullish(),
});

const TalentPoolDataSchema = z.object({
  id: z.string(),
  title: z.string(),
  slug: z.string(),
  status: z.enum(['draft', 'archived']),
  candidatesCount: z.number().int().nonnegative().nullish(),
});

const LinkDataSchema = z.object({
  id: z.string(),
  label: z.string(),
});

const SourceDataSchema = z.object({
  id: z.string(),
  title: z.string(),
  url: z.string(),
  provider: z.string().nullish(),
});

const EventDataSchema = z.object({
  id: z.string(),
  startsAt: z.string().datetime(),
  duration: z.number().int().positive().nullish(),
  candidate: AvatarSchema.nullish(),
  candidateId: z.string().nullish(),
  assignedJob: z.object({ title: z.string(), status: OfferStatusSchema }).nullish(),
});

const EvaluationDataSchema = z.object({
  id: z.string(),
  template: z.string(),
  candidate: AvatarSchema,
  candidateId: z.string(),
  scope: z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('job'), title: z.string(), status: OfferStatusSchema }),
    z.object({ kind: z.literal('multi-jobs'), count: z.number().int().positive() }),
  ]),
});

const TaskDataSchema = z.object({
  id: z.string(),
  title: z.string(),
  dueDate: z.string().datetime().nullish(),
  completed: z.boolean().nullish(),
  admins: z.array(AvatarSchema).max(10).nullish(),
  triggered: z.boolean().nullish(),
});

const RequisitionDataSchema = z.object({
  id: z.string(),
  title: z.string(),
  hiresCount: z.number().int().nonnegative(),
  openingsCount: z.number().int().positive(),
  status: z.enum(['draft', 'pending', 'rejected', 'approved', 'filled']),
  departmentName: z.string().nullish(),
});

export const ListItemSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('candidate'), data: CandidateDataSchema }),
  z.object({ kind: z.literal('team-member'), data: TeamMemberDataSchema }),
  z.object({ kind: z.literal('job'), data: JobDataSchema }),
  z.object({ kind: z.literal('talent-pool'), data: TalentPoolDataSchema }),
  z.object({ kind: z.literal('link'), data: LinkDataSchema }),
  z.object({ kind: z.literal('source'), data: SourceDataSchema }),
  z.object({ kind: z.literal('event'), data: EventDataSchema }),
  z.object({ kind: z.literal('evaluation'), data: EvaluationDataSchema }),
  z.object({ kind: z.literal('task'), data: TaskDataSchema }),
  z.object({ kind: z.literal('requisition'), data: RequisitionDataSchema }),
]);
